import type { Path } from 'react-hook-form'
import { z } from 'zod'

import { parseAmount } from '@/features/cash/item-form'
import { messages } from '@/lib/messages'
import type { CashSettings, CashSettingsWrite } from '@/services/http/cash'

const t = messages.cash.settings

function integerInRange(min: number, max: number) {
  return (text: string): boolean => {
    const trimmed = text.trim()
    if (!trimmed) return false
    const value = Number(trimmed)
    return Number.isInteger(value) && value >= min && value <= max
  }
}

const pessimisticDays = z.string().refine(integerInRange(0, 180), t.pessimisticInvalid)

/** Sınıf ve grup carisi çiftleri sunucuda sözlüktür (`{kod: değer}`); formda sıralı liste olarak tutulur. */
const pair = z.object({ key: z.string(), value: z.string() })

/**
 * Nakit ayarları formunun değerleri. Skaler alanlar ve `types` `CashSettingsWrite` ile aynı adı taşır,
 * böylece sunucunun 422 yolu (`min_cash`, `types.0.day`, ...) eşleme olmadan alana düşer. Sayılar metin
 * olarak tutulur; `settingsBody` gövdeye çevirir.
 */
export const settingsFormSchema = z.object({
  min_cash: z.string().refine((text) => parseAmount(text) >= 0, t.minCashInvalid),
  payment_weekday: z.string().refine(integerInRange(1, 5)),
  payment_rule: z.string().min(1),
  // Boş = son gün yok.
  entry_deadline_weekday: z.string(),
  // Boş = yok; doluysa SS:DD (`entry_deadline_time` deseni).
  entry_deadline_time: z.string().regex(/^(([01]\d|2[0-3]):[0-5]\d)?$/, t.deadlineTimeInvalid),
  pessimistic_collection_days: pessimisticDays,
  pessimistic_cheque_days: pessimisticDays,
  types: z
    .array(
      z.object({
        name: z.string().min(1).max(100),
        direction: z.string(),
        // Boş = tarih kalemden gelir.
        day: z
          .string()
          .refine((text) => !text.trim() || integerInRange(1, 31)(text), t.typeDayInvalid),
        month_offset: z.string().refine(integerInRange(0, 12), t.monthOffsetInvalid),
      }),
    )
    .min(1, t.typesCount)
    .max(60, t.typesCount),
  classes: z.array(pair),
  group_caris: z.array(pair),
})

export type SettingsFormValues = z.infer<typeof settingsFormSchema>

export function settingsFormValues(settings: CashSettings): SettingsFormValues {
  return {
    min_cash: String(settings.min_cash),
    payment_weekday: String(settings.payment_weekday),
    payment_rule: settings.payment_rule,
    entry_deadline_weekday: settings.entry_deadline_weekday?.toString() ?? '',
    entry_deadline_time: settings.entry_deadline_time ?? '',
    pessimistic_collection_days: String(settings.pessimistic_collection_days ?? 14),
    pessimistic_cheque_days: String(settings.pessimistic_cheque_days ?? 7),
    types: settings.types.map((type) => ({
      name: type.name,
      direction: type.direction,
      day: type.day == null ? '' : String(type.day),
      month_offset: String(type.month_offset ?? 1),
    })),
    classes: Object.entries(settings.classes).map(([key, value]) => ({ key, value })),
    group_caris: Object.entries(settings.group_caris).map(([key, value]) => ({ key, value })),
  }
}

/**
 * Kaydet gövdesi; okunan sürüm (`version`) zorunludur. Boş kodlu sınıf satırları ve boş kod ya da boş
 * adlı grup carisi satırları atılır (sunucunun normalleştirdiği hâl kayıttan sonra forma geri yüklenir).
 */
export function settingsBody(
  firm: string,
  values: SettingsFormValues,
  version: number,
): CashSettingsWrite {
  return {
    firm,
    min_cash: parseAmount(values.min_cash),
    payment_weekday: Number(values.payment_weekday),
    payment_rule: values.payment_rule as CashSettingsWrite['payment_rule'],
    types: values.types.map((type) => ({
      name: type.name,
      direction: type.direction as CashSettingsWrite['types'][number]['direction'],
      day: type.day.trim() ? Number(type.day) : null,
      month_offset: Number(type.month_offset),
    })),
    classes: Object.fromEntries(
      values.classes.filter(({ key }) => key.trim()).map(({ key, value }) => [key, value]),
    ) as CashSettingsWrite['classes'],
    group_caris: Object.fromEntries(
      values.group_caris
        .filter(({ key, value }) => key.trim() && value.trim())
        .map(({ key, value }) => [key, value]),
    ),
    entry_deadline_weekday: values.entry_deadline_weekday
      ? Number(values.entry_deadline_weekday)
      : null,
    entry_deadline_time: values.entry_deadline_time || null,
    pessimistic_collection_days: Number(values.pessimistic_collection_days),
    pessimistic_cheque_days: Number(values.pessimistic_cheque_days),
    version,
  }
}

/**
 * Sunucunun sözlük yollu 422'sini (`classes.<kod>`: değer; `classes.<kod>.[key]`: kod) formdaki satıra
 * eşler (`useRecordEditor` `serverFields`). Diğer yollar (`types.0.day`, `min_cash`) zaten aynı adı taşır.
 */
export function settingsServerField(
  path: string,
  values: SettingsFormValues,
): Path<SettingsFormValues> | undefined {
  for (const name of ['classes', 'group_caris'] as const) {
    const prefix = `${name}.`
    if (!path.startsWith(prefix)) continue
    let key = path.slice(prefix.length)
    const keyError = key.endsWith('.[key]')
    if (keyError) key = key.slice(0, -'.[key]'.length)
    const index = values[name].findIndex((pair) => pair.key === key)
    if (index < 0) return undefined
    return `${name}.${index}.${keyError ? 'key' : 'value'}`
  }
  return undefined
}
